import { JackBurtonApi } from "./sdk";
import { ApiConfiguration, isResponseError } from "./api-configuration";
import { Ref, ref } from "vue";

export function useApi() {
  return new JackBurtonApi(ApiConfiguration);
}

export type SortDescending = boolean | null;

export type PaginatedLoaderOptions<T> = {
  sortBy: T | null;
  sortDescending: SortDescending;
  totalRecords: number;
  currentPage: number;
  pageSize: number;
};

export type ApiLoader<T> = {
  load: () => Promise<boolean>;

  data: Ref<T>;
  loading: Ref<boolean>;
  errored: Ref<boolean>;
  errorMessage: Ref<string | null>;
};

export type ApiLoaderFn<T> = () => Promise<T>;

export function useApiLoader<T>(
  loader: ApiLoaderFn<T>,
  defaultVal: T,
): ApiLoader<T> {
  const data = ref(defaultVal) as Ref<T>;
  const loading = ref(false);
  const errored = ref(false);
  const errorMessage = ref<string | null>(null);

  async function load() {
    try {
      loading.value = true;
      errored.value = false;
      data.value = await loader();
    } catch (e: any) {
      if (isResponseError(e)) {
        errored.value = true;
        errorMessage.value = await e.response.text();
      }
    } finally {
      loading.value = false;
    }
    return !errored.value;
  }

  return {
    load,

    data,
    loading,
    errored,
    errorMessage,
  };
}

export type PaginatedApiLoader<TRes, TSort> = ApiLoader<TRes[]> & {
  load: (options: PaginatedLoaderOptions<TSort>) => Promise<boolean>;

  sortBy: Ref<TSort | null>;
  sortDescending: Ref<SortDescending>;
  totalRecords: Ref<number>;
  currentPage: Ref<number>;
  pageSize: Ref<number>;
};

export type PaginatedApiLoaderFn<TRes, TSort> = (
  options: PaginatedLoaderOptions<TSort>,
) => Promise<{ data: TRes[]; totalRecords: number }>;

export function usePaginatedApiLoader<TRes, TSort>(
  loader: PaginatedApiLoaderFn<TRes, TSort>,
  defaultVal: TRes[],
): PaginatedApiLoader<TRes, TSort> {
  const sortBy = ref(null) as Ref<TSort>;
  const sortDescending = ref(null);
  const totalRecords = ref(0);
  const currentPage = ref(1);
  const pageSize = ref(20);

  const { data, loading, errored, errorMessage, load } = useApiLoader(
    async () => {
      const res = await loader({
        sortBy: sortBy.value,
        sortDescending: sortDescending.value,
        totalRecords: totalRecords.value,
        currentPage: currentPage.value,
        pageSize: pageSize.value,
      });
      totalRecords.value = res.totalRecords;
      return res.data;
    },
    defaultVal,
  );

  return {
    load,

    data,
    sortBy,
    sortDescending,
    totalRecords,
    currentPage,
    pageSize,
    loading,
    errored,
    errorMessage,
  };
}
